import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Rocket, ChevronRight, CheckCircle2, Sparkles } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { repBootstrap, repCreate, repUpdate, repPublish, repTestChat } from "@/lib/api";

const field = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500";
const btnPrimary = "inline-flex items-center gap-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-500 px-4 py-2.5 text-sm font-semibold transition";
const btnGhost = "inline-flex items-center gap-2 rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200 px-4 py-2.5 text-sm font-semibold transition";

const STEPS = [
  "Business identity",
  "Select the agent",
  "Channels",
  "Personality & voice",
  "Business knowledge",
  "Actions",
  "Escalation",
  "Testing",
  "Approval & launch",
  "Continuous improvement",
];

const AGENT_TYPES = [
  "Receptionist", "Sales", "Lead qualification", "Appointment",
  "Support", "Routing", "Reservation", "Order-status",
  "Voicemail", "Follow-up", "Custom",
];

export default function BuildAgentWizard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [boot, setBoot] = useState(null);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [rep, setRep] = useState(null);
  const [testMsg, setTestMsg] = useState("Hi, what services do you offer?");
  const [testResult, setTestResult] = useState(null);
  const [form, setForm] = useState({
    business_name: "", industry: "", website: "", hours: "9-5",
    agent_type: "Receptionist",
    channels: ["web"],
    tone: "friendly", voice: "alloy", greeting: "", language: "en",
    knowledge_desc: "",
    actions_on: ["create_lead", "request_callback", "transfer_human"],
    escalation: "",
    verified_authority: false,
  });

  useEffect(() => { if (user) repBootstrap().then(setBoot).catch(() => {}); }, [user]);
  if (user === null) return <div className="min-h-screen flex flex-col"><Navbar /><div className="flex-1 grid place-items-center"><Link to="/login" className={btnPrimary}>Sign in to build an agent</Link></div></div>;

  const totalSteps = STEPS.length;
  const canProceed = (() => {
    if (step === 0) return form.business_name.trim().length > 1;
    if (step === 8) return form.verified_authority;
    return true;
  })();

  const next = async () => {
    // On step 0 → auto-create the representative in DRAFT so subsequent steps update it
    if (step === 0 && !rep) {
      setSaving(true);
      try {
        const template = boot?.industry_templates?.find((t) => (t.label || "").toLowerCase().includes(form.industry.toLowerCase()));
        const r = await repCreate({
          name: form.agent_type,
          business_name: form.business_name,
          role: form.agent_type,
          package: "text",
          industry: form.industry,
          website: form.website,
          template_key: template?.key || null,
        });
        setRep(r);
      } finally { setSaving(false); }
    }
    // On last step → publish
    if (step === totalSteps - 1) {
      setSaving(true);
      try {
        await repUpdate(rep.id, {
          verified_authority: true,
          channels: form.channels,
          languages: [form.language],
          personality: {
            tone: form.tone,
            greeting: form.greeting || `Hi, I'm your ${form.agent_type.toLowerCase()}. How can I help?`,
            voice: form.voice,
          },
          actions: form.actions_on.reduce((acc, k) => ({...acc, [k]: {enabled: true, requires_confirmation: false, requires_human_approval: false}}), {}),
        });
        await repPublish(rep.id);
        navigate(`/representative/${rep.id}`);
        return;
      } catch (e) {
        alert(e?.response?.data?.detail || "Publish failed");
      } finally { setSaving(false); }
    }
    // On middle steps → persist partial updates
    if (rep && step > 0 && step < totalSteps - 1) {
      setSaving(true);
      try {
        const patch = { channels: form.channels, languages: [form.language] };
        if (step === 3) patch.personality = { tone: form.tone, greeting: form.greeting, voice: form.voice };
        if (step === 5) patch.actions = form.actions_on.reduce((acc, k) => ({...acc, [k]: {enabled: true, requires_confirmation: false, requires_human_approval: false}}), {});
        await repUpdate(rep.id, patch);
      } finally { setSaving(false); }
    }
    setStep(Math.min(step + 1, totalSteps - 1));
  };

  const runTest = async () => {
    if (!rep) return;
    setSaving(true);
    try {
      const r = await repTestChat(rep.id, { message: testMsg });
      setTestResult(r);
    } finally { setSaving(false); }
  };

  const toggleList = (list, v) => setForm({...form, [list]: form[list].includes(v) ? form[list].filter((x) => x !== v) : [...form[list], v]});

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1 pt-24 pb-16" data-testid="wizard-page">
        <div className="max-w-3xl mx-auto px-6">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-indigo-700">
            <Rocket className="w-3.5 h-3.5" /> Build Your AI Employee
          </div>
          <h1 className="mt-2 text-3xl font-bold text-slate-900">Create a new agent in 10 steps</h1>

          <div className="mt-6 flex gap-1 overflow-x-auto pb-2">
            {STEPS.map((s, i) => (
              <div key={i} className={`text-[10px] font-bold uppercase px-2 py-1 rounded ${i === step ? "bg-indigo-600 text-white" : i < step ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`} data-testid={`step-${i}`}>{i + 1}. {s}</div>
            ))}
          </div>

          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6">
            {step === 0 && (
              <div className="space-y-3" data-testid="wizard-step-0">
                <Field label="Business name"><input value={form.business_name} onChange={(e) => setForm({...form, business_name: e.target.value})} className={field} data-testid="w-business" /></Field>
                <Field label="Industry"><input value={form.industry} onChange={(e) => setForm({...form, industry: e.target.value})} className={field} data-testid="w-industry" /></Field>
                <Field label="Website"><input value={form.website} onChange={(e) => setForm({...form, website: e.target.value})} className={field} data-testid="w-website" /></Field>
                <Field label="Business hours"><input value={form.hours} onChange={(e) => setForm({...form, hours: e.target.value})} className={field} data-testid="w-hours" /></Field>
              </div>
            )}
            {step === 1 && (
              <div className="space-y-2" data-testid="wizard-step-1">
                <div className="text-sm text-slate-600 mb-2">What kind of agent is this?</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {AGENT_TYPES.map((a) => (
                    <button key={a} onClick={() => setForm({...form, agent_type: a})} data-testid={`w-agent-${a.split(' ')[0].toLowerCase()}`}
                      className={`rounded-lg border px-3 py-2 text-sm ${form.agent_type === a ? "bg-indigo-50 border-indigo-500 text-indigo-700" : "bg-white border-slate-200 text-slate-700"}`}>{a}</button>
                  ))}
                </div>
              </div>
            )}
            {step === 2 && (
              <div data-testid="wizard-step-2">
                <div className="text-sm text-slate-600 mb-2">Which channels can this agent use?</div>
                <div className="flex flex-wrap gap-2">
                  {["web", "voice", "phone", "sms", "whatsapp", "email"].map((c) => (
                    <button key={c} onClick={() => toggleList("channels", c)} data-testid={`w-ch-${c}`}
                      className={`rounded-full px-3 py-1.5 text-xs font-semibold border ${form.channels.includes(c) ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-700 border-slate-300"}`}>{c}</button>
                  ))}
                </div>
                <div className="mt-3 text-[11px] text-slate-500">Voice / phone / WhatsApp require credentials (mocked in this environment).</div>
              </div>
            )}
            {step === 3 && (
              <div className="space-y-3" data-testid="wizard-step-3">
                <Field label="Greeting"><input value={form.greeting} onChange={(e) => setForm({...form, greeting: e.target.value})} placeholder="Hi, how can I help?" className={field} data-testid="w-greeting" /></Field>
                <Field label="Tone"><select value={form.tone} onChange={(e) => setForm({...form, tone: e.target.value})} className={field} data-testid="w-tone">{["friendly", "formal", "reassuring", "educational", "sales", "concise"].map((t) => <option key={t}>{t}</option>)}</select></Field>
                <Field label="Voice"><select value={form.voice} onChange={(e) => setForm({...form, voice: e.target.value})} className={field} data-testid="w-voice">{["alloy", "nova", "shimmer", "onyx", "echo", "fable"].map((v) => <option key={v}>{v}</option>)}</select></Field>
                <Field label="Language"><select value={form.language} onChange={(e) => setForm({...form, language: e.target.value})} className={field} data-testid="w-language">{["en", "fr", "es", "hi", "ml", "ta", "de", "pt"].map((l) => <option key={l}>{l}</option>)}</select></Field>
              </div>
            )}
            {step === 4 && (
              <div data-testid="wizard-step-4">
                <div className="text-sm text-slate-600 mb-2">The agent will only answer from approved knowledge. You can manage sources in the WebsiteTech Center after publishing.</div>
                <Field label="Describe what knowledge you'll upload later"><textarea rows={3} value={form.knowledge_desc} onChange={(e) => setForm({...form, knowledge_desc: e.target.value})} className={field} data-testid="w-knowledge" /></Field>
              </div>
            )}
            {step === 5 && (
              <div data-testid="wizard-step-5">
                <div className="text-sm text-slate-600 mb-2">Which actions can the agent perform? (You can refine per-action rules later.)</div>
                <div className="grid grid-cols-2 gap-2">
                  {["create_lead", "request_callback", "book_appointment", "transfer_human", "notify_staff", "recommend_product", "check_service_area", "generate_summary", "explain_invoice", "start_secure_payment"].map((a) => (
                    <button key={a} onClick={() => toggleList("actions_on", a)} data-testid={`w-act-${a}`}
                      className={`rounded border px-3 py-2 text-xs font-semibold text-left ${form.actions_on.includes(a) ? "bg-emerald-50 border-emerald-500 text-emerald-700" : "bg-white border-slate-200 text-slate-700"}`}>{a}</button>
                  ))}
                </div>
              </div>
            )}
            {step === 6 && (
              <div data-testid="wizard-step-6">
                <Field label="Escalate to a human when…"><textarea rows={3} value={form.escalation} onChange={(e) => setForm({...form, escalation: e.target.value})} placeholder="Low confidence · negative sentiment · medical/legal/financial questions · urgency" className={field} data-testid="w-escalation" /></Field>
                <div className="mt-2 text-[11px] text-slate-500">Restricted topics (medical/legal/financial) auto-refuse and escalate by policy.</div>
              </div>
            )}
            {step === 7 && (
              <div data-testid="wizard-step-7">
                <div className="text-sm text-slate-600 mb-2">Test your agent (preview mode — works even in draft).</div>
                <div className="flex gap-2">
                  <input value={testMsg} onChange={(e) => setTestMsg(e.target.value)} className={field} data-testid="w-test-msg" />
                  <button onClick={runTest} disabled={saving || !rep} className={btnPrimary} data-testid="w-test-btn">Send</button>
                </div>
                {testResult && (
                  <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm" data-testid="w-test-result">
                    <div className="text-slate-800">{testResult.reply}</div>
                    <div className="mt-2 text-[10px] text-slate-500">Grounding: {testResult.hallucination?.level} · {testResult.hallucination?.score} · Language: {testResult.language}</div>
                  </div>
                )}
              </div>
            )}
            {step === 8 && (
              <div data-testid="wizard-step-8">
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 mb-3">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.verified_authority} onChange={(e) => setForm({...form, verified_authority: e.target.checked})} className="mt-1" data-testid="w-verified" />
                    <span className="text-sm text-amber-900"><b>I confirm I have authority to create an AI representative for {form.business_name}.</b> Impersonating another business or person is prohibited.</span>
                  </label>
                </div>
                <div className="text-xs text-slate-500">AI disclosure, recording notice, PII redaction and retention are enabled by default. You can adjust them in the Studio's Consent tab.</div>
              </div>
            )}
            {step === 9 && (
              <div data-testid="wizard-step-9" className="space-y-2">
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
                  <div className="text-sm text-emerald-900">
                    <b>Ready to publish.</b> After launch, review unanswered questions, failed actions and customer ratings in the Studio's Analytics tab to keep improving.
                  </div>
                </div>
                <div className="text-xs text-slate-500">Clicking Publish will make the agent live on the channels you selected.</div>
              </div>
            )}
          </div>

          <div className="mt-6 flex justify-between">
            <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} className={btnGhost} data-testid="w-back">← Back</button>
            <button onClick={next} disabled={!canProceed || saving} className={btnPrimary} data-testid="w-next">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : step === totalSteps - 1 ? <Rocket className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              {step === totalSteps - 1 ? "Publish" : "Next"}
            </button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Field({ label, children }) {
  return <div><label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">{label}</label><div className="mt-1">{children}</div></div>;
}
